export const DEFAULT_LOCALE = 'en-AU' as const
export const GERMAN_LOCALE = 'de-DE' as const
export const RTL_LOCALES = ['ar', 'he', 'fa', 'ur'] as const

export type LocaleDirection = 'ltr' | 'rtl'

export interface VisibleLanguageOption {
  locale: string
  nativeName: string
  slug?: string
  canonical?: boolean
}

interface LanguageRemoteConfigValue {
  visibleLanguages?: Array<VisibleLanguageOption>
}
interface LanguageAlternateLink {
  hrefLang: string
  href: string
}

function getMatchingLanguageOption(
  locale: string,
  languageOptions: Array<VisibleLanguageOption>,
): VisibleLanguageOption | undefined {
  const normalisedLocale = locale.replace(/_/g, '-')
  const exactMatch = languageOptions.find((option) => {
    return normalisedLocale === option.slug || normalisedLocale === option.locale
  })
  if (exactMatch) return exactMatch

  // where locale is unsupported outside the main version
  // it should redirect to base locale prefix like fr-CA to fr
  const language = normalisedLocale.split('-')[0]
  return languageOptions.find((option) => {
    return option.slug === language || option.locale.split('-')[0] === language
  })
}

export function getCountryCodeFromLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const match = getMatchingLanguageOption(locale, languageOptions)
  if (match) return new Intl.Locale(match.locale).region
}

export function getNativeNameForLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const match = getMatchingLanguageOption(locale, languageOptions)
  if (match) return match.nativeName
}

export function getNormalisedLocale(
  unnormalisedLocale: string,
  languageOptions?: Array<VisibleLanguageOption>,
): string | null {
  if (!languageOptions?.length) return null
  return resolveLocale(unnormalisedLocale, languageOptions).normalised
}

export function resolveLocale(
  locale: string | null | undefined,
  languageOptions: Array<VisibleLanguageOption>,
): {
  full: string | null
  normalised: string | null
} {
  if (!locale) return { full: null, normalised: null }

  const match = getMatchingLanguageOption(locale, languageOptions)
  if (!match) return { full: null, normalised: null }

  return {
    full: match.locale,
    normalised: match.slug ?? match.locale,
  }
}

export function getVisibleLanguageOptions(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): Array<VisibleLanguageOption> {
  return getLanguageRemoteConfigValue(remoteConfig)?.visibleLanguages ?? []
}

export function getLocaleDirection(
  locale: string | null | undefined,
): LocaleDirection {
  if (!locale) return 'ltr'
  const language = locale.replace(/_/g, '-').split('-')[0].toLowerCase()
  return RTL_LOCALES.some((rtlLocale) => rtlLocale === language) ? 'rtl' : 'ltr'
}

export function getLocaleLanguageCode(
  locale: string | null | undefined,
): string {
  return (locale ?? DEFAULT_LOCALE).replace(/_/g, '-').split('-')[0].toLowerCase()
}

/**
 * Arabic content in the customer portal uses Western digits for numeric values.
 * Preserve every other locale preference while applying that number-system policy.
 */
export function getLocaleWithWesternDigits(locale: string): string {
  if (getLocaleLanguageCode(locale) !== 'ar') return locale

  return new Intl.Locale(locale, { numberingSystem: 'latn' }).toString()
}

export function isEnglishLocale(locale: string | null | undefined): boolean {
  return getLocaleLanguageCode(locale) === 'en'
}

/**
 * Prefer an explicit non-default request locale; otherwise use the locale baked into
 * locale-specific browser bundles (`BUILD_LOCALE`) so formatting matches inlined translations.
 */
export function resolveActiveAppLocale(
  geoLocale: string | null | undefined,
): string | undefined {
  if (geoLocale && geoLocale !== DEFAULT_LOCALE) {
    return geoLocale
  }

  const buildLocale = process.env.BUILD_LOCALE
  if (buildLocale) {
    return buildLocale
  }

  return geoLocale ?? undefined
}

function getLanguageRemoteConfigValue(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): LanguageRemoteConfigValue | undefined {
  if (remoteConfig?.error) return
  const value = remoteConfig?.value
  if (!value || typeof value !== 'object' || Array.isArray(value)) return

  return value as LanguageRemoteConfigValue
}

export function getLanguageAlternateLinks({
  customerPortalURL,
  path,
  languageOptions,
}: {
  customerPortalURL: string
  path: string
  languageOptions: Array<VisibleLanguageOption>
}): Array<LanguageAlternateLink> {
  if (!languageOptions.length) return []

  const segments = path.split('/')
  const firstSegment = segments[1]
  const hasLocaleSegment = !!firstSegment && languageOptions.some(
    option => option.slug === firstSegment || option.locale === firstSegment,
  )
  const barePath = hasLocaleSegment
    ? ['', ...segments.slice(2)].join('/') || '/'
    : path

  return languageOptions.filter(option => option.locale !== DEFAULT_LOCALE).map(option => {
    const region = new Intl.Locale(option.locale).region?.toLowerCase() ?? option.locale.split('-').pop()?.toLowerCase() ?? ''
    const languageSegment = option.slug ?? option.locale
    return {
      hrefLang: option.locale,
      href: `${customerPortalURL}/${region}/${languageSegment}${barePath}`,
    }
  })
}
